import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Sparkles, ArrowLeft, Check } from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';

/**
 * Map a feature key to the plan that FIRST unlocks it (for marketing copy).
 * Keys not listed here fall through to a generic upgrade nudge.
 */
const FEATURE_TO_UNLOCK_PLAN = {
  // Pro unlocks
  has_website: { slug: 'professional', nameAr: 'المتقدمة', priceFromAr: 'من 2,990 ريال/شهر' },
  device_integration: { slug: 'professional', nameAr: 'المتقدمة', priceFromAr: 'من 2,990 ريال/شهر' },
  qc_module: { slug: 'professional', nameAr: 'المتقدمة', priceFromAr: 'من 2,990 ريال/شهر' },
  contractor_labs: { slug: 'professional', nameAr: 'المتقدمة', priceFromAr: 'من 2,990 ريال/شهر' },
  whatsapp_marketing: { slug: 'professional', nameAr: 'المتقدمة', priceFromAr: 'من 2,990 ريال/شهر' },
  microbiology: { slug: 'professional', nameAr: 'المتقدمة', priceFromAr: 'من 2,990 ريال/شهر' },
  results_analysis: { slug: 'professional', nameAr: 'المتقدمة', priceFromAr: 'من 2,990 ريال/شهر' },
  custom_branding: { slug: 'professional', nameAr: 'المتقدمة', priceFromAr: 'من 2,990 ريال/شهر' },
  multi_branch: { slug: 'professional', nameAr: 'المتقدمة', priceFromAr: 'من 2,990 ريال/شهر' },
  home_visits: { slug: 'professional', nameAr: 'المتقدمة', priceFromAr: 'من 2,990 ريال/شهر' },

  // Enterprise-only
  has_custom_domain: { slug: 'enterprise', nameAr: 'الاحترافية', priceFromAr: '5,990 ريال/شهر' },
  google_reviews: { slug: 'enterprise', nameAr: 'الاحترافية', priceFromAr: '5,990 ريال/شهر' },
  serial_tests: { slug: 'enterprise', nameAr: 'الاحترافية', priceFromAr: '5,990 ريال/شهر' },
  test_questionnaires: { slug: 'enterprise', nameAr: 'الاحترافية', priceFromAr: '5,990 ريال/شهر' },
  zatca_log: { slug: 'enterprise', nameAr: 'الاحترافية', priceFromAr: '5,990 ريال/شهر' },
  website_widget: { slug: 'enterprise', nameAr: 'الاحترافية', priceFromAr: '5,990 ريال/شهر' },
};

const FEATURE_LABELS = {
  has_website: 'الموقع الإلكتروني الخاص',
  has_custom_domain: 'الدومين المخصّص',
  device_integration: 'ربط أجهزة التحليل',
  qc_module: 'مراقبة الجودة (QC)',
  contractor_labs: 'مختبرات تعاقدية',
  whatsapp_marketing: 'تسويق واتساب',
  microbiology: 'الأحياء الدقيقة',
  results_analysis: 'تحليل النتائج المتقدّم',
  custom_branding: 'هوية بصرية مخصّصة',
  multi_branch: 'فروع متعددة',
  home_visits: 'الزيارات المنزلية',
  zatca_integration: 'فوترة ZATCA الإلكترونية',
  ai_analysis: 'تحليل نتائج AI',
  sms_notifications: 'إشعارات SMS',
  data_export: 'تصدير البيانات',
  google_reviews: 'مراجعات Google',
  serial_tests: 'التحاليل التسلسلية',
  test_questionnaires: 'استبيانات التحليل',
  zatca_log: 'سجل ZATCA التفصيلي',
  website_widget: 'وِدجِت الموقع',
};

/**
 * Large page-level upgrade prompt. Shown when a tenant lands on a page whose
 * feature is gated. Feature-aware: the copy mentions exactly which plan
 * unlocks this specific feature + what they get.
 */
export const UpgradePrompt = ({ feature }) => {
  const navigate = useNavigate();
  const { plan } = useSubscription();

  const unlock = feature ? FEATURE_TO_UNLOCK_PLAN[feature] : null;
  const featureLabel = feature ? FEATURE_LABELS[feature] : null;
  const currentSlug = plan?.slug || 'free';
  const onFreePlan = currentSlug === 'free';

  const headline = featureLabel
    ? `${featureLabel} — متاح في الباقة ${unlock?.nameAr || 'المدفوعة'}`
    : 'هذه الخاصية تتطلّب ترقية الاشتراك';

  const subline = unlock
    ? `ارقِ لباقة ${unlock.nameAr} (${unlock.priceFromAr}) لفتح ${featureLabel || 'هذه الخاصية'} و ميزات أخرى.`
    : onFreePlan
      ? 'أنت حالياً على الباقة التجريبية. ارقِ لباقة مدفوعة لفتح كل الميزات.'
      : 'رقّي اشتراكك للوصول لهذه الخاصية.';

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6" dir="rtl">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl text-center"
      >
        <div className="w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-xl shadow-blue-200">
          <Sparkles className="w-10 h-10 text-white" />
        </div>

        <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-3">
          {headline}
        </h1>
        <p className="text-slate-500 mb-8 max-w-lg mx-auto">
          {subline}
        </p>

        {/* What you get bullets */}
        {unlock?.slug === 'professional' && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-8 text-start">
            <div className="text-xs font-semibold text-blue-600 mb-3">في الباقة المتقدمة:</div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm text-slate-700">
              {[
                'موقع إلكتروني كامل',
                'ربط أجهزة التحليل (HL7/ASTM)',
                'مراقبة الجودة (QC)',
                '3 فروع، 15 مستخدم',
                'تسويق واتساب مدمج',
                'الأحياء الدقيقة',
                'مختبرات تعاقدية',
                'AI: 10,000 تحليل/شهر',
              ].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {unlock?.slug === 'enterprise' && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-8 text-start">
            <div className="text-xs font-semibold text-purple-600 mb-3">في الباقة الاحترافية:</div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm text-slate-700">
              {[
                'كل ميزات المتقدمة',
                'دومين مخصّص (example.com)',
                'فروع ومستخدمين غير محدودين',
                'AI: 20,000 تحليل/شهر',
                'تكامل Google Reviews',
                'سجل ZATCA التفصيلي',
                'استبيانات التحليل',
                'دعم مخصّص 24/7',
              ].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => navigate('/settings/subscription/upgrade')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-200"
          >
            ترقية الاشتراك
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-2 px-5 py-3 border border-slate-200 text-slate-600 rounded-xl font-semibold hover:bg-slate-50"
          >
            <Lock className="w-4 h-4" />
            رجوع
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Usage: <FeatureGate feature="has_website"><WebsiteRouter /></FeatureGate>
const FeatureGate = ({ feature, children, fallback }) => {
  const { canAccessFeature, loading } = useSubscription();

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!canAccessFeature(feature)) {
    return fallback || <UpgradePrompt feature={feature} />;
  }

  return children;
};

export default FeatureGate;
